import { Button, CircularProgress } from "@material-ui/core";
import DropdownFilter from "components/DropdownFilter";
import NotificationProvider from "components/NotificationProvider";
import DateTimeField from "components/DateTimePicker/DateTimePicker";
import CardHeader from "features/mechanicalWork/cardHeader";
import {
    useAcceptCancellationMutation,
    useCancelWorkRequestMutation,
    useCompleteWorkRequestMutation,
    useDelayWorkOrderMutation,
    useRejectCancellationMutation,
    useRejectWorkRequestMutation,
    useSendEstimateMutation,
    useSendTechnicianMutation,
    useSimulateDryRunMutation
} from "features/workRequest/workRequestSlice";
import {
    actualArrivalLabel,
    arrivalTypes,
    cancellationReasonLabel,
    cancellationReasonOptions,
    delayReasonLabel,
    delayReasonOptions,
    expectedArrivalLabel,
    rejectionReasonLabel,
    rejectionReasonOptions,
    workRequestButtonLabels,
    workRequestSectionIds,
    workRequestSections
} from "features/workRequest/workRequestConsts";
import { IconClipboard } from "icons";
import { useState } from "react";
import SendEstimateModal from "../SendEstimateModal/SendEstimateModal";
import { workRequestCardStyles } from "./WorkRequestCardStyles";

const toArrivalTime = (value) => {
    const date = new Date(value);
    if (isNaN(date.getTime())) return null;
    date.setSeconds(0, 0);
    return date.toISOString();
}

const WorkRequestCard = ({ workRequestId, isWorkOrderCancelled }) => {
    const classes = workRequestCardStyles();

    const [expectedArrivalTime, setExpectedArrivalTime] = useState(new Date());
    const [actualArrivalTime, setActualArrivalTime] = useState(new Date());
    const [rejectionReason, setRejectionReason] = useState('');
    const [delayReason, setDelayReason] = useState('');
    const [cancellationReason, setCancellationReason] = useState('');
    const [isEstimateOpen, setIsEstimateOpen] = useState(false);

    const [sendTechnicianEnRoute, { isLoading: isSendingEnRoute }] = useSendTechnicianMutation();
    const [sendTechnicianArrival, { isLoading: isSendingArrival }] = useSendTechnicianMutation();
    const [completeWorkRequest, { isLoading: isCompleting }] = useCompleteWorkRequestMutation();
    const [simulateDryRun, { isLoading: isSimulatingDryRun }] = useSimulateDryRunMutation();
    const [rejectWorkRequest, { isLoading: isRejecting }] = useRejectWorkRequestMutation();
    const [delayWorkOrder, { isLoading: isDelaying }] = useDelayWorkOrderMutation();
    const [cancelWorkRequest, { isLoading: isCancelling }] = useCancelWorkRequestMutation();
    const [acceptCancellation, { isLoading: isAcceptingCancellation }] = useAcceptCancellationMutation();
    const [rejectCancellation, { isLoading: isRejectingCancellation }] = useRejectCancellationMutation();
    const [sendEstimate, { isLoading: isSendingEstimate }] = useSendEstimateMutation();

    const actions = [
        {
            label: workRequestButtonLabels.TECHNICIAN_EN_ROUTE,
            section: workRequestSectionIds.DISPATCH,
            id: workRequestId,
            trigger: sendTechnicianEnRoute,
            isLoading: isSendingEnRoute,
            arrival: { label: expectedArrivalLabel, time: expectedArrivalTime, onChange: setExpectedArrivalTime },
            payload: {
                workRequestId,
                arrivalType: arrivalTypes.EXPECTED_ARRIVAL,
                arrivalTime: toArrivalTime(expectedArrivalTime)
            }
        },
        {
            label: workRequestButtonLabels.TECHNICIAN_ARRIVAL,
            section: workRequestSectionIds.DISPATCH,
            id: workRequestId,
            trigger: sendTechnicianArrival,
            isLoading: isSendingArrival,
            arrival: { label: actualArrivalLabel, time: actualArrivalTime, onChange: setActualArrivalTime },
            payload: {
                workRequestId,
                arrivalType: arrivalTypes.ACTUAL_ARRIVAL,
                arrivalTime: toArrivalTime(actualArrivalTime)
            }
        },
        {
            label: workRequestButtonLabels.COMPLETE,
            section: workRequestSectionIds.PROGRESS,
            id: workRequestId,
            trigger: completeWorkRequest,
            isLoading: isCompleting,
            payload: { workRequestId }
        },
        {
            label: workRequestButtonLabels.DRY_RUN,
            section: workRequestSectionIds.PROGRESS,
            id: workRequestId,
            trigger: simulateDryRun,
            isLoading: isSimulatingDryRun,
            payload: { workRequestId }
        },
        {
            label: workRequestButtonLabels.SEND_ESTIMATE,
            section: workRequestSectionIds.PROGRESS,
            id: workRequestId,
            isLoading: isSendingEstimate,
            onClick: () => setIsEstimateOpen(true)
        },
        {
            label: workRequestButtonLabels.REJECT,
            section: workRequestSectionIds.EXCEPTIONS,
            id: workRequestId,
            trigger: rejectWorkRequest,
            isLoading: isRejecting,
            reason: {
                label: rejectionReasonLabel,
                options: rejectionReasonOptions,
                value: rejectionReason,
                onChange: setRejectionReason
            },
            payload: { workRequestId, rejectionReason: Number(rejectionReason) }
        },
        {
            label: workRequestButtonLabels.DELAY,
            section: workRequestSectionIds.EXCEPTIONS,
            id: workRequestId,
            trigger: delayWorkOrder,
            isLoading: isDelaying,
            reason: {
                label: delayReasonLabel,
                options: delayReasonOptions,
                value: delayReason,
                onChange: setDelayReason
            },
            payload: { workRequestId, reason: Number(delayReason) }
        },
        {
            label: workRequestButtonLabels.CANCEL,
            section: workRequestSectionIds.EXCEPTIONS,
            id: workRequestId,
            trigger: cancelWorkRequest,
            isLoading: isCancelling,
            reason: {
                label: cancellationReasonLabel,
                options: cancellationReasonOptions,
                value: cancellationReason,
                onChange: setCancellationReason
            },
            payload: { workRequestId, reason: Number(cancellationReason) }
        },
        {
            label: workRequestButtonLabels.ACCEPT_CANCELLATION,
            section: workRequestSectionIds.CANCELLATION,
            id: workRequestId,
            trigger: acceptCancellation,
            isLoading: isAcceptingCancellation,
            enabled: !!isWorkOrderCancelled,
            payload: { workRequestId }
        },
        {
            label: workRequestButtonLabels.REJECT_CANCELLATION,
            section: workRequestSectionIds.CANCELLATION,
            id: workRequestId,
            trigger: rejectCancellation,
            isLoading: isRejectingCancellation,
            enabled: !!isWorkOrderCancelled,
            payload: { workRequestId }
        },
    ];

    const sections = workRequestSections
        .map(section => ({ ...section, items: actions.filter(action => action.section === section.id) }))
        .filter(section => section.items.length > 0);

    const onAction = async ({ label, trigger, payload }) => {
        const result = await trigger(payload);
        if (result?.error) {
            NotificationProvider.error(`${label} failed`);
        } else {
            NotificationProvider.success(`${label} succeeded`);
        }
        return result;
    };

    const onSendEstimate = async (amounts) => {
        const result = await onAction({
            label: workRequestButtonLabels.SEND_ESTIMATE,
            trigger: sendEstimate,
            payload: { workRequestId, ...amounts }
        });
        if (!result?.error) {
            setIsEstimateOpen(false);
        }
    };

    const isDisabled = (action) => !action.id
        || action.isLoading
        || action.enabled === false
        || (!!action.arrival && !toArrivalTime(action.arrival.time))
        || (!!action.reason && !action.reason.value);

    return (
        <>
            <CardHeader title='Work Request' icon={<IconClipboard />} />

            <div className={classes.sections}>
                {sections.map(section => {
                    const hasInputs = section.items.some(item => item.arrival || item.reason);
                    return (
                        <div className={classes.section} key={section.id}>
                            <div className={classes.sectionLabel}>{section.label}</div>
                            <div className={hasInputs ? classes.inputRows : classes.buttonRow}>
                                {section.items.map(action => (
                                    <div className={classes.actionRow} key={action.label}>
                                        {action.arrival &&
                                            <div className={classes.field}>
                                                <DateTimeField
                                                    label={action.arrival.label}
                                                    value={action.arrival.time}
                                                    onChange={action.arrival.onChange}
                                                />
                                            </div>
                                        }
                                        {action.reason &&
                                            <DropdownFilter
                                                title={action.reason.label}
                                                values={action.reason.options}
                                                value={action.reason.value}
                                                onSelect={action.reason.onChange}
                                                classStyles={classes.field}
                                                showEmpty
                                            />
                                        }
                                        <Button
                                            variant="outlined"
                                            className={classes.actionBtn}
                                            disabled={isDisabled(action)}
                                            onClick={() => action.onClick ? action.onClick() : onAction(action)}
                                            startIcon={action.isLoading ? <CircularProgress size={14} /> : undefined}
                                        >
                                            {action.label}
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>

            <SendEstimateModal
                open={isEstimateOpen}
                onClose={() => setIsEstimateOpen(false)}
                onSend={onSendEstimate}
                isSending={isSendingEstimate}
            />
        </>
    )
}

export default WorkRequestCard;
